import { useEffect, useState } from "react";
import { Users, TrendingUp, Coins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/token/stats`;
const REFRESH_MS = 60 * 1000;

function fmtNumber(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return Number(n).toLocaleString();
}

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  // very small price
  const e = -Math.floor(Math.log10(n));
  return `$${n.toExponential(2)}`;
}

export default function LiveStats() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await fetch(API, { cache: "no-store" });
        if (!res.ok) throw new Error("bad status");
        const data = await res.json();
        if (!cancelled) {
          setStats(data);
          setErr(false);
        }
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    };
    fetchStats();
    const id = setInterval(fetchStats, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const items = [
    {
      id: "holders",
      icon: Users,
      label: "HOLDERS",
      value: fmtNumber(stats?.holder_count),
      accent: "#00F0FF",
    },
    {
      id: "mcap",
      icon: TrendingUp,
      label: "MARKET CAP",
      value: fmtUsd(stats?.market_cap),
      accent: "#FFD700",
    },
    {
      id: "price",
      icon: Coins,
      label: "PRICE",
      value: fmtUsd(stats?.usd_price),
      accent: "#9B5CFF",
    },
  ];

  return (
    <section
      data-testid="live-stats-bar"
      className="relative py-10 border-y border-white/10 bg-[#08080F]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <div className="font-mono text-[10px] tracking-[0.32em] text-[#00F0FF] flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#00F0FF] animate-pulse" />
            // LIVE · ON-CHAIN
          </div>
          <div className="font-mono text-[10px] tracking-[0.32em] text-white/35">
            {err ? "STALE" : "AUTO-REFRESH 60s"}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`live-stat-${it.id}`}
              className="flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-4 sm:py-5 border border-white/10 bg-[#0B0B12]"
            >
              <div
                className="hidden sm:flex w-10 h-10 items-center justify-center border"
                style={{ borderColor: `${it.accent}55`, color: it.accent, background: `${it.accent}0D` }}
              >
                <it.icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.32em] text-white/45">
                  {it.label}
                </div>
                <div
                  className="mt-1 font-heading text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-white truncate"
                  title={it.value}
                >
                  {stats == null && !err ? "…" : it.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
